import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Lock, FolderOpen, Plus, Settings, LogOut, User, FileText } from 'lucide-react';
import { authApi } from '../services/api';

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const currentUser = authApi.getCurrentUserLocal();
    setUser(currentUser);
    setShowUserMenu(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setShowUserMenu(false);
    authApi.logout();
    navigate('/login');
  };

  // Don't show header on login page
  if (location.pathname === '/login') {
    return null;
  }

  // If user is not authenticated, redirect to login
  if (!authApi.isAuthenticated()) {
    return null;
  }
  
  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-3 group">
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                <img 
                src="/assets/os-logo-w-text.png" 
                alt="Omniscope"
                className="h-12 text-primary-500"
              />
              </h1>
              <div className="flex items-center space-x-1 text-xs text-gray-600 ml-[0.65rem]">
                <Lock className="w-3 h-3" aria-hidden="true" />
                <span>Secure File Transfer</span>
              </div>
            </div>
          </Link>
          
          {/* Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/dashboard"
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/dashboard') 
                  ? 'text-black bg-primary-100 border-l-4 border-primary-500' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              aria-current={isActive('/dashboard') ? 'page' : undefined}
            >
              <FolderOpen className="w-4 h-4" aria-hidden="true" />
              <span>Dashboard</span>
            </Link>

            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive('/admin') 
                    ? 'text-black bg-primary-100 border-l-4 border-primary-500' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                aria-current={isActive('/admin') ? 'page' : undefined}
              >
                <Settings className="w-4 h-4" aria-hidden="true" />
                <span>Admin</span>
              </Link>
            )}
            
            <Link
              to="/create-request"
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              <span>New Request</span>
            </Link>

            {user && user.role === 'admin' && (
              <a
                href={`${(process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/?$/, '')}/api/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
              >
                <FileText className="w-4 h-4" aria-hidden="true" />
                <span>API Docs</span>
              </a>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
              >
                <User className="w-4 h-4" aria-hidden="true" />
                <span>{user?.username}</span>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs text-gray-500 border-b">
                      {user?.email}
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-1 px-2 py-1 text-sm text-gray-600"
            >
              <User className="w-4 h-4" />
              <span>{user?.username}</span>
            </button>
          </div>
        </div>
        
        {/* Mobile navigation */}
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 bg-gray-50 -mx-4">
            <Link
              to="/dashboard"
              className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium ${
                isActive('/dashboard') 
                  ? 'text-primary-700 bg-primary-100' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-current={isActive('/dashboard') ? 'page' : undefined}
            >
              <FolderOpen className="w-5 h-5" aria-hidden="true" />
              <span>Dashboard</span>
            </Link>

            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium ${
                  isActive('/admin') 
                    ? 'text-primary-700 bg-primary-100' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                aria-current={isActive('/admin') ? 'page' : undefined}
              >
                <Settings className="w-5 h-5" aria-hidden="true" />
                <span>Admin</span>
              </Link>
            )}
            
            <Link
              to="/create-request"
              className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Plus className="w-5 h-5" aria-hidden="true" />
              <span>New Request</span>
            </Link>

            {user && user.role === 'admin' && (
              <a
                href={`${(process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/?$/, '')}/api/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <FileText className="w-5 h-5" aria-hidden="true" />
                <span>API Docs</span>
              </a>
            )}

            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 w-full text-left"
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
}
